/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Icon, LinearProgress, SxProps, Theme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

export interface IDialogActions {
    id: string;
    text: string;
    icon: string;
    iconColor: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | undefined;
    isClose?: boolean | true;
    setAction: () => void;
}

interface IDialogFormProps {
    children: React.ReactNode;
    title: string;
    icon: string;
    iconColor?: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | undefined;
    setIsOpen: (status: boolean) => void;
    isOpen: boolean;
    fullWidth: boolean;
    maxWidth: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    actions: IDialogActions[] | undefined;
    loading?: boolean;
    paperSx?: SxProps<Theme>;
}

export const DialogForm: React.FC<IDialogFormProps> = ({ children, title, icon, iconColor, setIsOpen, isOpen, fullWidth, maxWidth, actions, loading, paperSx }) => {
    const [isOpenLocal, setIsOpenLocal] = useState(isOpen);
    const descriptionElementRef = useRef<HTMLElement>(null);
    const basePaperSx = {
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 18px 48px rgba(16, 24, 40, 0.16)',
    };
    const dialogPaperSx = (paperSx
        ? Array.isArray(paperSx)
            ? [basePaperSx, ...paperSx]
            : [basePaperSx, paperSx]
        : basePaperSx) as SxProps<Theme>;

    useEffect(() => {
        if (isOpen) {
            setIsOpenLocal(true);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpenLocal) {
            const { current: descriptionElement } = descriptionElementRef;
            if (descriptionElement !== null) {
                descriptionElement.focus();
            }
        }
    }, [isOpenLocal]);

    const handleClose = (action: any) => {
        if (action.isClose != false) {
            setIsOpen(false);
            setIsOpenLocal(false);
        }

        try {
            action.setAction();
        } catch (error) {
            undefined;
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            setIsOpen(false);
            setIsOpenLocal(false);
        }
    };

    return (

        <React.Fragment>
            <Dialog
                open={isOpenLocal}
                onClose={undefined}
                onKeyDown={handleKeyDown}
                scroll='paper'
                aria-labelledby="scroll-dialog-title"
                aria-describedby="scroll-dialog-description"
                fullWidth={fullWidth}
                maxWidth={maxWidth}
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={{ display: 'flex', flexShrink: 0, color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }} ml={-1} mt={0} p={2} id="scroll-dialog-title" alignItems='center' gap={1}>
                    <Icon color={iconColor}>{icon}</Icon>
                    {title}
                </DialogTitle>
                <DialogContent sx={{ p: 2, flex: paperSx ? 1 : undefined, minHeight: paperSx ? 0 : undefined, overflow: paperSx ? 'hidden' : undefined }} dividers={true} >
                    {loading ? (
                        <LinearProgress sx={{ borderRadius: 3 }} />
                    ) : (
                        <Box sx={{ display: 'flex', gap: 1, overflow: 'hidden', paddingTop: .6, height: paperSx ? '100%' : undefined, minHeight: paperSx ? 0 : undefined, width: '100%' }}>
                            {children}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, flexShrink: 0, borderTop: '1px solid', borderColor: 'divider' }}>
                    {actions ? actions.map((action, index) => {
                        return (
                            <Button
                                key={index}
                                id={action.id}
                                size='small'
                                variant='outlined'
                                color={action.iconColor}
                                startIcon={<Icon>{action.icon}</Icon>}
                                onClick={() => handleClose(action)}
                                sx={{
                                    fontWeight: 700,
                                    '@media print': {
                                        display: 'none !important',
                                    },
                                }}
                            >
                                {action.text}
                            </Button>
                        );
                    }) : undefined}
                </DialogActions>
            </Dialog>
        </React.Fragment >
    );
};
